import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Light / dark / system appearance.
 *
 * "system" is the default and is not a stored colour - it follows the OS and
 * has to keep following it, so the media query stays subscribed for as long as
 * that mode is selected rather than being read once at startup.
 */
export type ThemeMode = "light" | "dark" | "system";

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = "healthpass-theme";

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Tailwind is configured with darkMode: "class", so this class is the switch. */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  const dark = mode === "dark" || (mode === "system" && prefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: "system",
      setMode: (mode) => {
        applyTheme(mode);
        set({ mode });
      },
    }),
    {
      name: STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        applyTheme(state?.mode ?? "system");
      },
    },
  ),
);

/**
 * Keeps "system" honest: without this the class is only correct at load, and
 * changing the OS appearance while the app is open does nothing.
 */
export function watchSystemTheme(): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (useThemeStore.getState().mode === "system") applyTheme("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
